import React, { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import { FaBolt, FaCogs, FaWater, FaCalendarCheck, FaGraduationCap, FaArrowRight, FaEuroSign, FaBuilding, FaUser, FaPhone, FaEnvelope } from "react-icons/fa";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const Projects = () => {
    const [scrollY, setScrollY] = useState(0);

    // Colors for consistent theming
    const primaryTextColor = "text-[#212529]"; // Dark text
    const secondaryTextColor = "text-[#212529]/80"; // Medium gray
    const tertiaryTextColor = "text-[#212529]/60"; // Light gray

    // Parallax scroll effect
    useEffect(() => {
        const handleScroll = () => {
            setScrollY(window.scrollY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const [headerRef, headerInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [projectsRef, projectsInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
        rootMargin: "-50px",
    });

    const [detailsRef, detailsInView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const [ctaRef, ctaInView] = useInView({
        triggerOnce: true,
        threshold: 0.2,
        rootMargin: "-50px",
    });

    return (
        <div className="min-h-screen font-sans overflow-hidden relative">
            <SEO 
                title="EU Funded Projects - Axialis Development"
                description="Axialis development is co-financed by the European Union through the Recovery and Resilience Facility. Development of New Axial Flux Electric Motor Technology."
                keywords="EU funded project, Axialis development, NPOO, Recovery and Resilience Facility, electric motor research, innovation Croatia"
                path="/projects"
            />
            {/* Hero Section */}
            <section
                ref={headerRef}
                className="relative pt-32 pb-8"
            >
                <div className="container mx-auto px-4 relative z-10">
                    <div className={`text-center transition-all duration-1000 transform ${headerInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${primaryTextColor}`}>
                            Axial Flux <span className="text-primary">Motor Technology</span>
                        </h1>
                        <div className="h-0.5 w-16 bg-primary mx-auto mb-6"></div>
                        <p className={`text-lg md:text-xl ${secondaryTextColor} mb-10 leading-relaxed`}>
                            The development of new axial flux electric motor technology
                        </p>
                    </div>
                </div>
            </section>

            {/* Project Details Section */}
            <section
                ref={detailsRef}
                className="pt-8 pb-16 relative overflow-hidden"
                style={{ zIndex: 10 }}
            >
                <div className="container mx-auto px-4 relative z-10">
                    <div className={`backdrop-blur-sm bg-white/60 rounded-xl overflow-hidden shadow-xl border border-white/40 transition-all duration-1000 transform ${detailsInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
                        <div className="p-2 md:p-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
                                <div className="space-y-4">
                                    <div className="flex items-center">
                                        <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md border border-white/40 mr-4">
                                            <FaBuilding className="text-primary text-xl" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-[#212529]/60 font-medium">Coordinator</p>
                                            <p className="text-[#212529] font-semibold">Axialis j.d.o.o.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md border border-white/40 mr-4">
                                            <FaEuroSign className="text-primary text-xl" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-[#212529]/60 font-medium">Total Project Value</p>
                                            <p className="text-[#212529] font-semibold">360.745,01 euro</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md border border-white/40 mr-4">
                                            <FaEuroSign className="text-primary text-xl" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-[#212529]/60 font-medium">Total Eligible Costs</p>
                                            <p className="text-[#212529] font-semibold">358.714,34 euro</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <div className="flex items-center">
                                        <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md border border-white/40 mr-4">
                                            <FaEuroSign className="text-primary text-xl" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-[#212529]/60 font-medium">EU Contribution to Project Funding</p>
                                            <p className="text-[#212529] font-semibold">198.874,17 euro</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md border border-white/40 mr-4">
                                            <FaCalendarCheck className="text-primary text-xl" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-[#212529]/60 font-medium">Implementation Period</p>
                                            <p className="text-[#212529] font-semibold">01.01.2024. – 30.06.2026.</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="bg-white/60 backdrop-blur-sm p-3 rounded-lg shadow-md border border-white/40 mr-4">
                                            <FaUser className="text-primary text-xl" />
                                        </div>
                                        <div>
                                            <p className="text-sm text-[#212529]/60 font-medium">Project Manager and Contact Person</p>
                                            <div className="flex items-center">
                                                <p className="text-[#212529] font-semibold mr-2">Marko Đula, director</p>
                                                <a href="mailto:mdjula00@gmail.com" className="text-primary hover:text-primary-dark transition-colors">
                                                    <FaEnvelope className="text-sm" />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6 mb-10">
                                <p className="text-[#212529]/80 leading-relaxed">
                                    The company Axialis Ltd. commenced the implementation of the project: Development of New Axial Flux Electric Motor Technology of a spin-off venture Axialis j.d.o.o., on January 1, 2024. This project is being carried out in accordance with the Grant Agreement for projects funded by the Recovery and Resilience Facility, signed with the Ministry of Science and Education and the Croatian Agency for Small Business, Innovation, and Investment. The project is co-financed by the European Union through the Recovery and Resilience Facility instrument.
                                </p>
                                <p className="text-[#212529]/80 leading-relaxed">
                                    The project "Development of New Axial Flux Electric Motor Technology by Axialis Ltd." aims to advance the research of the project team for the development, testing, and protection of a prototype axial flux motor with innovative cooling technology and significantly improved geometry optimization. This will enable broader applications of axial flux motors. The anticipated results will enhance the performance of electric motors and electric vehicles. The project activities, including additional training, will increase the company's capacity, improve the competencies of young researchers, and strengthen the link between the scientific and private sectors.
                                </p>
                            </div>

                            <h3 className={`text-3xl font-medium mb-4 ${primaryTextColor}`}>Project Activities</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div className="backdrop-blur-sm bg-white/40 p-6 rounded-xl shadow-lg border border-white/40 flex">
                                    <div className="bg-white/60 backdrop-blur-sm p-3 rounded-full shadow-md border border-white/40 mr-4 h-fit">
                                        <FaCogs className="text-primary text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-medium text-[#212529] mb-2">Optimized Geometry</h4>
                                        <p className="text-[#212529]/70 text-sm">Designing and testing the optimized geometry of the axial flux electric motor</p>
                                    </div>
                                </div>
                                <div className="backdrop-blur-sm bg-white/40 p-6 rounded-xl shadow-lg border border-white/40 flex">
                                    <div className="bg-white/60 backdrop-blur-sm p-3 rounded-full shadow-md border border-white/40 mr-4 h-fit">
                                        <FaWater className="text-primary text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-medium text-[#212529] mb-2">Cooling System</h4>
                                        <p className="text-[#212529]/70 text-sm">Designing, testing, and patenting a new cooling system for the axial flux electric motor</p>
                                    </div>
                                </div>
                                <div className="backdrop-blur-sm bg-white/40 p-6 rounded-xl shadow-lg border border-white/40 flex">
                                    <div className="bg-white/60 backdrop-blur-sm p-3 rounded-full shadow-md border border-white/40 mr-4 h-fit">
                                        <FaGraduationCap className="text-primary text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-medium text-[#212529] mb-2">Business Development</h4>
                                        <p className="text-[#212529]/70 text-sm">Business development and training for young researchers</p>
                                    </div>
                                </div>
                                <div className="backdrop-blur-sm bg-white/40 p-6 rounded-xl shadow-lg border border-white/40 flex">
                                    <div className="bg-white/60 backdrop-blur-sm p-3 rounded-full shadow-md border border-white/40 mr-4 h-fit">
                                        <FaBolt className="text-primary text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-medium text-[#212529] mb-2">Project Management</h4>
                                        <p className="text-[#212529]/70 text-sm">Comprehensive project management and promotion activities</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-10 flex justify-center">
                                <div className="flex flex-col md:flex-row items-center space-y-4 md:space-y-0 md:space-x-4 bg-white/60 backdrop-blur-sm p-6 rounded-xl border border-white/40 shadow-md">
                                    <img src="/images/eu-funding-logo.jpg" alt="Funded by the European Union - NextGenerationEU" className="h-16" />
                                    <div className="text-center md:text-left">
                                        <p className="text-[#212529] font-medium mb-1">Co-financed by the European Union</p>
                                        <p className="text-[#212529]/70 text-sm max-w-md">
                                            This project is co-financed by the European Union through the Recovery and Resilience Facility instrument.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action Section */}
            <section
                ref={ctaRef}
                className="py-20 relative"
                style={{ zIndex: 10 }}
            >
                <div className="container mx-auto px-4 text-center">
                    <div
                        className={`max-w-3xl mx-auto transition-all duration-1000 transform ${ctaInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                    >
                        <div className="backdrop-blur-sm bg-white/40 rounded-xl p-8 shadow-xl border border-white/40">
                            <h3 className="text-2xl md:text-3xl font-medium mb-4 text-[#212529]">
                                Interested in Our Motor Technology Development?
                            </h3>
                            <p className="text-[#212529]/80 mb-8">
                                Learn more about our cutting-edge axial flux motor technology or get in touch to discuss potential collaborations and applications.
                            </p>
                            <div className="flex flex-wrap justify-center gap-4">
                                <Link
                                    to="/contact"
                                    className="bg-primary/90 hover:bg-primary text-white backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Contact Us
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                                <Link
                                    to="/explore-motor"
                                    className="bg-white/70 hover:bg-white/80 text-[#212529] backdrop-blur-sm shadow-xl border border-white/40 px-6 py-3 rounded-lg font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group flex items-center"
                                >
                                    Explore Our Motor
                                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Projects; 